import type { EventRecord, OrderRecord } from "@/types/records";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Ticket,
} from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  formatEventDateTimeRange,
  resolveEventTimeZone,
} from "@/lib/event-timezone";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import {
  TicketDownloadCard,
  type TicketDownloadData,
} from "@/components/checkout/TicketDownloadCard";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { resolveTicketQrCode } from "@/lib/ticket-qr";
import { invokeAuthenticatedFunction } from "@/lib/api-certificates";
import {
  ensureAbsoluteUrl,
  getEventPublicPath,
  getLiveRedirectUrl,
} from "@/lib/urls";
import { TransferTicketModal } from "@/components/TransferTicketModal";
import { ResaleListingModal } from "@/components/ResaleListingModal";
import { ContactProducerModal } from "@/components/ContactProducerModal";

export default function TicketDetailPage() {
  const { t } = useTranslation("buyer");
  const { ticketId } = useParams();

  // Realtime: auto-update ticket detail when ticket or its order changes
  useRealtimeSubscription({
    table: "tickets",
    filter: ticketId ? `id=eq.${ticketId}` : undefined,
    queryKey: ["my-ticket-detail"],
    enabled: !!ticketId,
  });
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [copying, setCopying] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [resaleModalOpen, setResaleModalOpen] = useState(false);
  const [contactProducerOpen, setContactProducerOpen] = useState(false);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["my-ticket-detail", user?.id, ticketId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tickets")
        .select(
          "id, event_id, tier_id, qr_code, qr_code_image_url, attendee_name, attendee_cpf, order_id, created_at, status, ticket_tiers(name, price, is_resellable, is_transferable), events(title, slug, cover_image_url, start_date, end_date, venue_name, venue_address, venue_city, venue_state, is_online, online_url, streaming_url, streaming_redirect_token, description, producer_id, organization_id, allow_resale, profiles(full_name, organizer_slug), organizations(slug)), orders(id, status, total, subtotal, platform_fee, discount_amount, created_at, payment_method, boleto_url, boleto_barcode)",
        )
        .eq("id", ticketId!)
        .eq("owner_id", user!.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !!ticketId,
  });

  const event = ticket?.events as EventRecord;
  const order = ticket?.orders as OrderRecord;

  const isOrg = !!event?.organization_id && !!event?.organizations?.slug;
  const producerSlug = isOrg ? event?.organizations?.slug : event?.profiles?.organizer_slug;
  const publicEventPath = event?.slug
    ? getEventPublicPath(event.slug, producerSlug, isOrg)
    : "";
  const eventUrl = publicEventPath
    ? `${window.location.origin}${publicEventPath}`
    : "";

  const liveRedirectUrl = event?.streaming_redirect_token
    ? getLiveRedirectUrl(event.streaming_redirect_token)
    : ensureAbsoluteUrl(event?.online_url);

  const ticketDownloadData = useMemo<TicketDownloadData | null>(() => {
    if (!ticket || !event) return null;

    return {
      ticketId: ticket.id,
      tierName: ticket.ticket_tiers?.name || t("ticketDetail.card.headerLabel"),
      tierPrice: ticket.ticket_tiers?.price,
      attendeeName: ticket.attendee_name || undefined,
      attendeeCpf: ticket.attendee_cpf || undefined,
      qrCode: resolveTicketQrCode(ticket.qr_code, ticket.id),
      qrCodeImageUrl: ticket.qr_code_image_url,
      eventTitle: event.title || "Evento",
      eventDate: event.start_date || new Date().toISOString(),
      eventEndDate: event.end_date,
      venueName: event.venue_name,
      venueAddress: event.venue_address,
      venueCity: event.venue_city,
      venueState: event.venue_state,
      isOnline: event.is_online,
      coverImageUrl: event.cover_image_url,
      streamingRedirectUrl: liveRedirectUrl || undefined,
      orderCode: (order?.id || ticket.order_id || ticket.id).slice(0, 8).toUpperCase(),
      purchaseDate: order?.created_at || ticket.created_at,
      paymentMethod: order?.payment_method || "cortesia",
      eventDescription: event.description || undefined,
      index: 0,
    };
  }, [ticket, event, order, liveRedirectUrl, t]);

  const handleCopyLink = async () => {
    if (!liveRedirectUrl) return;
    try {
      setCopying(true);
      await navigator.clipboard.writeText(liveRedirectUrl);
      toast({
        title: t("ticketDetail.toasts.linkCopiedTitle"),
        description: t("ticketDetail.toasts.linkCopiedDesc"),
      });
    } catch {
      toast({ title: t("ticketDetail.toasts.copyErrorTitle"), variant: "destructive" });
    } finally {
      setCopying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container pt-6 lg:pt-24 pb-16 max-w-5xl">
        <div className="space-y-4">
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-6 w-72" />
          <Skeleton className="h-[520px] w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!ticket || !event) {
    return (
      <div className="container pt-6 lg:pt-24 pb-16 max-w-5xl">
        <SEOHead
          title={t("ticketDetail.seoNotFoundTitle")}
          description={t("ticketDetail.seoNotFoundDesc")}
        />
        <EmptyState
          icon={<Ticket className="h-12 w-12" />}
          title={t("ticketDetail.notFoundTitle")}
          description={t("ticketDetail.notFoundDesc")}
          actionLabel={t("ticketDetail.backToTickets")}
          onAction={() => navigate("/meus-ingressos")}
        />
      </div>
    );
  }

  const eventStart = new Date(event.start_date);
  const eventEnd = event.end_date ? new Date(event.end_date) : null;
  const eventTimeZone = resolveEventTimeZone((event).time_zone);

  return (
    <>
      <SEOHead
        title={`${event.title || t("ticketDetail.card.headerLabel")}`}
        description={
          event.description ||
          t("ticketDetail.accessConfirmation")
        }
      />
      <div className="container pt-4 lg:pt-12 pb-16 max-w-5xl animate-custom-fade-in">
        <div className="mb-8 space-y-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/meus-ingressos")}
            className="gap-2 -ml-2 text-muted-foreground hover:text-foreground transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("ticketDetail.backToTickets")}
          </Button>

          <div className="flex flex-col gap-1">
            <p className="text-[10px] text-muted-foreground font-medium">
              {t("ticketDetail.accessConfirmation")}
            </p>
            <h1 className="font-display text-2xl lg:text-3xl font-semibold text-foreground tracking-tight line-clamp-2">
              {event.title}
            </h1>
          </div>
        </div>

        {order?.payment_method === "boleto" && order?.boleto_url && (
          <div className="mb-8 p-4 md:p-5 rounded-2xl border border-warning/20 bg-warning/5 text-warning-foreground flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="space-y-1">
              <p className="font-medium text-warning flex items-center gap-2">
                {t("ticketDetail.boletoBannerTitle")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("ticketDetail.boletoBannerDesc")}
              </p>
            </div>
            <Button size="sm" asChild className="shrink-0 whitespace-nowrap rounded-xl">
              <Link to={`/pedido/${order?.id}`}>{t("ticketDetail.payBoleto")}</Link>
            </Button>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-[1fr_420px] items-stretch">
          <section className="flex flex-col gap-6">
            <div className="flex-1 rounded-2xl border border-border bg-card p-6 sm:p-8 flex flex-col">
              <div className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[10px] font-medium text-muted-foreground">
                        {t("ticketDetail.dateTimeLabel")}
                      </p>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-[10px] text-primary font-medium"
                        asChild
                      >
                        <a
                          href={`https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${new Date(event.start_date).toISOString().replace(/-|:|\.\d\d\d/g, "")}/${new Date(event.end_date || event.start_date).toISOString().replace(/-|:|\.\d\d\d/g, "")}&details=${encodeURIComponent("Acesse seus ingressos em tickethall.com.br")}&location=${encodeURIComponent(event.is_online ? t("ticketDetail.onlineAccess") : event.venue_name || "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("ticketDetail.addToCalendar")}
                        </a>
                      </Button>
                    </div>
                    <p className="text-foreground font-medium">
                      {formatEventDateTimeRange(
                        eventStart,
                        eventEnd,
                        eventTimeZone,
                        { dateStyle: "long" },
                      )}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-[10px] font-medium text-muted-foreground">
                      {t("ticketDetail.locationLabel")}
                    </p>
                    <p className="text-foreground font-medium">
                      {event.is_online
                        ? t("ticketDetail.onlineAccess")
                        : [event.venue_name, event.venue_city, event.venue_state]
                            .filter(Boolean)
                            .join(", ") || t("ticketDetail.venueToConfirm")}
                    </p>
                  </div>
                </div>

                {event.is_online && (
                  <div className="rounded-2xl border border-border bg-muted/20 p-6 space-y-4">
                    <p className="text-sm font-medium text-foreground">
                      {t("ticketDetail.onlineEventLinkTitle")}
                    </p>
                    {liveRedirectUrl ? (
                      <div className="space-y-4">
                        <div className="break-all text-xs font-mono text-muted-foreground bg-background rounded-xl border border-border p-4">
                          {liveRedirectUrl}
                        </div>
                        <div className="flex flex-wrap gap-3">
                          <Button
                            asChild
                            className="rounded-xl px-6"
                          >
                            <a
                              href={liveRedirectUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("ticketDetail.accessEvent")}
                            </a>
                          </Button>
                          <Button
                            variant="outline"
                            className="rounded-xl px-6"
                            onClick={handleCopyLink}
                            disabled={copying}
                          >
                            {copying ? t("ticketDetail.copying") : t("ticketDetail.copyLink")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-background/50 rounded-xl border border-dashed border-border">
                        <p className="text-sm text-muted-foreground">
                          {t("ticketDetail.onlineLinkPending")}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions Section */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  className={`rounded-xl h-11 text-xs ${!ticket.ticket_tiers?.is_transferable ? "opacity-40 grayscale" : ""}`}
                  onClick={() => {
                    if (!ticket.ticket_tiers?.is_transferable) {
                      toast({
                        title: t("ticketDetail.toasts.transferUnavailableTitle"),
                        description: t("ticketDetail.toasts.transferUnavailableDesc"),
                      });
                      return;
                    }
                    setTransferModalOpen(true);
                  }}
                >
                  {t("ticketDetail.actions.transfer")}
                </Button>
                <Button
                  variant="outline"
                  className={`rounded-xl h-11 text-xs ${!(ticket.ticket_tiers?.is_resellable && ticket.events?.allow_resale) ? "opacity-40 grayscale" : ""}`}
                  onClick={() => {
                    if (!ticket.ticket_tiers?.is_resellable || !ticket.events?.allow_resale) {
                      toast({
                        title: t("ticketDetail.toasts.resaleUnavailableTitle"),
                        description: t("ticketDetail.toasts.resaleUnavailableDesc"),
                      });
                      return;
                    }
                    setResaleModalOpen(true);
                  }}
                >
                  {t("ticketDetail.actions.resell")}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-xl h-11 text-xs"
                  disabled={resendingEmail}
                  onClick={async () => {
                    if (!ticket || !event) return;
                    setResendingEmail(true);
                    try {
                      const payload = {
                        ticketId: ticket.id,
                        recipientEmail: user?.email,
                        eventTitle: event.title,
                        tierName: ticket.ticket_tiers?.name,
                        qrCode: resolveTicketQrCode(ticket.qr_code, ticket.id),
                      };
                      await invokeAuthenticatedFunction(
                        "send-ticket-email",
                        payload,
                        session?.access_token ?? undefined,
                      );
                      toast({
                        title: t("ticketDetail.toasts.emailSentTitle"),
                        description: t("ticketDetail.toasts.emailSentDesc"),
                      });
                    } catch (err) {
                      toast({
                        title: t("ticketDetail.toasts.emailErrorTitle"),
                        description: err?.message || t("ticketDetail.toasts.emailErrorDesc"),
                        variant: "destructive",
                      });
                    } finally {
                      setResendingEmail(false);
                    }
                  }}
                >
                  {resendingEmail ? t("ticketDetail.actions.sendingEmail") : t("ticketDetail.actions.resendEmail")}
                </Button>
                {event.producer_id && (
                  <Button
                    variant="outline"
                    className="rounded-xl h-11 text-xs"
                    onClick={() => setContactProducerOpen(true)}
                  >
                    Falar com o produtor
                  </Button>
                )}
              </div>

              {/* Event Page Banner at the bottom of the card */}
              <div className="mt-auto pt-4">
                <div className="p-4 rounded-xl border border-border bg-muted/10 flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <p className="text-[10px] font-medium text-muted-foreground">{t("ticketDetail.officialSiteBanner.title")}</p>
                    <p className="text-xs text-foreground/80">{t("ticketDetail.officialSiteBanner.description")}</p>
                  </div>
                  <Button
                    asChild
                    variant="link"
                    size="sm"
                    className="text-primary p-0 h-auto font-semibold"
                  >
                    <a
                      href={
                        eventUrl ||
                        getEventPublicPath(
                          event.slug,
                          producerSlug,
                          isOrg,
                        )
                      }
                      target={eventUrl ? "_blank" : undefined}
                      rel={eventUrl ? "noopener noreferrer" : undefined}
                    >
                      {t("ticketDetail.officialSiteBanner.link")}
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <aside className="flex flex-col gap-6">
            <div className="h-full rounded-2xl border border-border bg-card p-6 sm:p-8 flex items-center justify-center">
              <TicketDownloadCard {...ticketDownloadData!} />
            </div>
          </aside>
        </div>
      </div>

      <TransferTicketModal
        open={transferModalOpen}
        onOpenChange={setTransferModalOpen}
        ticketId={ticket.id}
        eventTitle={event.title}
        tierName={ticket.ticket_tiers?.name || ""}
      />

      <ResaleListingModal
        open={resaleModalOpen}
        onOpenChange={setResaleModalOpen}
        ticket={{
          ...ticket,
          event_id: ticket.event_id,
          tier_id: ticket.tier_id,
        }}
      />

      {event.producer_id && (
        <ContactProducerModal
          open={contactProducerOpen}
          onOpenChange={setContactProducerOpen}
          producerId={event.producer_id}
          producerName={event.profiles?.full_name || "produtor"}
          eventId={event.id}
          eventTitle={event.title}
        />
      )}
    </>
  );
}
